import { useEffect, useState } from 'react';
import { Box, Card, CardContent, Grid, Typography, CircularProgress, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../../services/adminService';
import type { EmailQueueStatsDto } from '../../types/admin';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

/**
 * Email Queue Statistics component
 * Displays queue health metrics including counts by status
 */
export default function EmailQueueStats() {
  const { t } = useTranslation('admin');
  const { token } = useAuth();
  const [stats, setStats] = useState<EmailQueueStatsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
    // Refresh every 30 seconds
    const interval = setInterval(loadStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadStats = async () => {
    if (!token) return;

    try {
      setError(null);
      const data = await adminService.getEmailQueueStats(token);
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !stats) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!stats) return null;

  const statCards = [
    {
      title: t('emailQueue.stats.pending'),
      value: stats.totalPending,
      icon: <MailOutlineIcon fontSize="large" />,
      color: '#1976d2'
    },
    {
      title: t('emailQueue.stats.retrying'),
      value: stats.totalRetrying,
      icon: <ScheduleIcon fontSize="large" />,
      color: '#ed6c02'
    },
    {
      title: t('emailQueue.stats.delivered'),
      value: stats.totalDelivered,
      icon: <CheckCircleOutlineIcon fontSize="large" />,
      color: '#2e7d32'
    },
    {
      title: t('emailQueue.stats.failed'),
      value: stats.totalFailed,
      icon: <ErrorOutlineIcon fontSize="large" />,
      color: '#d32f2f'
    }
  ];

  return (
    <Grid container spacing={3}>
      {statCards.map((stat, index) => (
        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    {stat.title}
                  </Typography>
                  <Typography variant="h4" component="div">
                    {stat.value.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ color: stat.color }}>
                  {stat.icon}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}
